/**
 * Public marketing landing page — served at `/`.
 *
 * Visual treatment is intentionally high-end: animated gradient orbs in the
 * hero, scroll-triggered reveal animations on every section, an animated
 * integration marquee, and hover-lift cards. All animations gracefully
 * collapse under `prefers-reduced-motion` via globals.css.
 *
 * Content is fetched from `GET /landing/content` (no auth required). On any
 * fetch failure we fall back to `DEFAULT_LANDING_CONTENT` so the page still
 * renders for first-time visitors before the singleton row is upserted.
 */
import {
  DEFAULT_LANDING_CONTENT,
  LEAD_LIMITS,
  type CreateLeadRequest,
  type LandingContent,
  type LandingContentView,
  type LandingFeature,
} from '@peerless/shared';
import { useQuery } from '@tanstack/react-query';
import * as LucideIcons from 'lucide-react';
import {
  AlertCircle,
  ArrowRight,
  Bot,
  CalendarDays,
  CheckCircle2,
  Contact,
  LayoutTemplate,
  LifeBuoy,
  Loader2,
  LogIn,
  Mail,
  MapPin,
  Mailbox,
  PhoneCall,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
  Table2,
  Wand2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { ApiError, apiRequest } from '../../lib/api-client';
import { cn } from '../../lib/cn';


// ---------------------------------------------------------------------------
// Reveal-on-scroll hook — lightweight IntersectionObserver. No external deps,
// no library overhead. Sets `revealed=true` the first time the element scrolls
// into view; the parent attaches a `pp-animate-reveal` class accordingly.
// ---------------------------------------------------------------------------

function useReveal<T extends HTMLElement = HTMLDivElement>(
  options: IntersectionObserverInit = { threshold: 0.15, rootMargin: '0px 0px -80px 0px' },
): { ref: React.RefObject<T | null>; revealed: boolean } {
  const ref = useRef<T>(null);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || revealed) return;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
          return;
        }
      }
    }, options);
    observer.observe(node);
    return () => observer.disconnect();
  }, [revealed, options]);
  return { ref, revealed };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function LandingPage(): JSX.Element {
  const { user } = useAuth();

  const contentQuery = useQuery({
    queryKey: ['landing', 'content'],
    queryFn: () => apiRequest<LandingContentView>('/landing/content'),
    staleTime: 60_000,
  });

  const content: LandingContent = contentQuery.data?.content ?? DEFAULT_LANDING_CONTENT;
  const dashboardHref = user?.role === 'admin' ? '/admin/users' : '/chat';

  return (
    <div className="min-h-screen bg-paper text-ink antialiased">
      <NavBar
        user={user}
        dashboardHref={dashboardHref}
        primaryCta={user ? 'Open dashboard' : content.hero.primaryCtaText}
        primaryCtaHref={user ? dashboardHref : content.hero.primaryCtaHref}
      />

      <Hero content={content} loggedInDashboardHref={user ? dashboardHref : null} />

      <IntegrationsMarquee />

      <FeaturesSection content={content} />

      <AboutSection content={content} />

      <ContactSection content={content} />

      <Footer content={content} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nav bar
// ---------------------------------------------------------------------------

function NavBar({
  user,
  dashboardHref,
  primaryCta,
  primaryCtaHref,
}: {
  user: { id: string; role: string } | null;
  dashboardHref: string;
  primaryCta: string;
  primaryCtaHref: string;
}): JSX.Element {
  // Add a stronger backdrop after scroll so the nav stays legible over the
  // animated hero gradient.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'sticky top-0 z-30 transition-all duration-300',
        scrolled
          ? 'border-b border-rule/60 bg-paper/85 backdrop-blur-md shadow-sm supports-[backdrop-filter]:bg-paper/65'
          : 'border-b border-transparent bg-transparent',
      )}
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-6">
        <Link to="/" className="flex items-center gap-2.5 transition hover:opacity-90">
          <img
            src="/peerless-logo.png"
            alt="Peerless"
            className="h-7 w-auto select-none invert dark:invert-0 dark:drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]"
            draggable={false}
          />
          <span className="hidden text-sm font-semibold tracking-tight text-ink sm:inline">
            AI Portal
          </span>
        </Link>
        <nav className="hidden items-center gap-6 text-sm text-ink-muted md:flex">
          <a href="#features" className="transition hover:text-ink">
            Features
          </a>
          <a href="#about" className="transition hover:text-ink">
            About
          </a>
          <a href="#contact" className="transition hover:text-ink">
            Contact
          </a>
        </nav>
        <div className="flex items-center gap-2">
          {user ? (
            <Link
              to={dashboardHref}
              className="inline-flex items-center gap-1.5 rounded-tight bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90"
            >
              <ArrowRight className="size-3.5" />
              {primaryCta}
            </Link>
          ) : (
            <Link
              to={primaryCtaHref}
              className="inline-flex items-center gap-1.5 rounded-tight bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg transition hover:opacity-90"
            >
              <LogIn className="size-3.5" />
              {primaryCta}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Hero — gradient orbs + grid + animated CTA
// ---------------------------------------------------------------------------

function Hero({
  content,
  loggedInDashboardHref,
}: {
  content: LandingContent;
  loggedInDashboardHref: string | null;
}): JSX.Element {
  const h = content.hero;
  return (
    <section className="relative isolate overflow-hidden">
      {/* Floating gradient orbs — pure CSS, GPU-accelerated transforms. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <div
          className="pp-animate-float-slow absolute -top-20 left-[8%] size-[420px] rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgb(var(--color-accent) / 0.35) 0%, transparent 65%)',
          }}
        />
        <div
          className="pp-animate-float absolute right-[5%] top-32 size-[360px] rounded-full blur-3xl"
          style={{
            animationDelay: '-2s',
            background:
              'radial-gradient(circle, rgb(var(--color-accent) / 0.20) 0%, transparent 65%)',
          }}
        />
        <div
          className="pp-animate-pulse-glow absolute bottom-[-120px] left-1/2 size-[520px] -translate-x-1/2 rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgb(var(--color-accent) / 0.18) 0%, transparent 60%)',
          }}
        />
      </div>

      {/* Background grid — masked to fade out at the edges */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(rgb(var(--color-text)) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--color-text)) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 80%)',
        }}
      />

      <div className="mx-auto max-w-5xl px-6 py-28 text-center sm:py-36 lg:py-44">
        {h.eyebrow && (
          <p
            className="pp-animate-reveal inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-accent"
            style={{ animationDelay: '0.05s' }}
          >
            <span className="size-1.5 rounded-full bg-accent shadow-[0_0_8px_rgb(var(--color-accent))]" />
            {h.eyebrow}
          </p>
        )}
        <h1
          className="pp-animate-reveal mt-6 text-balance text-4xl font-semibold tracking-tight text-ink sm:text-6xl lg:text-7xl"
          style={{ animationDelay: '0.15s' }}
        >
          {h.title}
        </h1>
        <p
          className="pp-animate-reveal mx-auto mt-6 max-w-2xl text-pretty text-base leading-relaxed text-ink-muted sm:text-lg"
          style={{ animationDelay: '0.3s' }}
        >
          {h.subtitle}
        </p>
        <div
          className="pp-animate-reveal mt-10 flex flex-wrap items-center justify-center gap-3"
          style={{ animationDelay: '0.45s' }}
        >
          <Link
            to={loggedInDashboardHref ?? h.primaryCtaHref}
            className={cn(
              'group pp-shimmer-overlay relative inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-medium text-accent-fg shadow-lg shadow-accent/30 transition hover:scale-[1.02] hover:shadow-xl hover:shadow-accent/40',
              'pp-animate-gradient-x',
            )}
            style={{
              backgroundImage:
                'linear-gradient(110deg, rgb(var(--color-accent)) 0%, rgb(var(--color-accent) / 0.85) 50%, rgb(var(--color-accent)) 100%)',
            }}
          >
            {loggedInDashboardHref ? 'Open dashboard' : h.primaryCtaText}
            <ArrowRight className="size-4 transition group-hover:translate-x-0.5" />
          </Link>
          {h.secondaryCtaText && h.secondaryCtaHref && (
            <a
              href={h.secondaryCtaHref}
              className="inline-flex items-center gap-2 rounded-md border border-rule bg-paper/60 px-6 py-3 text-sm font-medium text-ink backdrop-blur transition hover:border-accent/50 hover:bg-paper-soft"
            >
              {h.secondaryCtaText}
            </a>
          )}
        </div>

        {/* Hero stats — small confidence-building strip */}
        <div
          className="pp-animate-reveal mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-rule bg-rule/50 sm:grid-cols-4"
          style={{ animationDelay: '0.6s' }}
        >
          <HeroStat value="8" label="AI surfaces" />
          <HeroStat value="40+" label="Generative models" />
          <HeroStat value="6" label="Support channels" />
          <HeroStat value="1" label="Workspace" />
        </div>
      </div>
    </section>
  );
}

function HeroStat({ value, label }: { value: string; label: string }): JSX.Element {
  return (
    <div className="bg-paper/80 p-4 backdrop-blur-sm">
      <p className="text-2xl font-semibold tracking-tight text-ink sm:text-3xl">{value}</p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-ink-muted">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Integrations marquee — the trust-strip
// ---------------------------------------------------------------------------

const INTEGRATIONS = [
  'OpenAI',
  'n8n',
  'Google Drive',
  'fal.ai',
  'kie.ai',
  'templated.io',
  'Buffer',
  'imgBB',
  'WhatsApp',
  'Telegram',
  'Meta',
  'IMAP',
];

function IntegrationsMarquee(): JSX.Element {
  // Duplicate the list so the translateX(-50%) loop is seamless.
  const doubled = [...INTEGRATIONS, ...INTEGRATIONS];
  return (
    <section className="relative overflow-hidden border-y border-rule/40 bg-paper-soft/30 py-8">
      <p className="mb-5 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
        Wired into the tools you already use
      </p>
      <div
        className="relative"
        style={{
          maskImage:
            'linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%)',
        }}
      >
        <div className="pp-animate-marquee flex w-max gap-12 whitespace-nowrap">
          {doubled.map((name, i) => (
            <span
              key={`${name}-${i}`}
              className="text-base font-semibold tracking-tight text-ink-muted/80"
            >
              {name}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Features — staggered reveal grid
// ---------------------------------------------------------------------------

type IconComponent = React.ComponentType<{ className?: string | undefined }>;

const FALLBACK_ICONS: IconComponent[] = [
  Bot,
  PhoneCall,
  Wand2,
  Sparkles,
  LifeBuoy,
  LayoutTemplate,
  Mailbox,
  ShieldCheck,
  Contact,
  CalendarDays,
  Table2,
];

function IconFor(name: string, fallback: IconComponent): JSX.Element {
  const Lookup = (LucideIcons as unknown as Record<string, IconComponent | undefined>)[name];
  const Icon = Lookup ?? fallback;
  return <Icon className="size-5" />;
}

function FeaturesSection({ content }: { content: LandingContent }): JSX.Element {
  const f = content.features;
  const { ref, revealed } = useReveal<HTMLDivElement>();
  return (
    <section id="features" className="relative border-b border-rule/40">
      <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
        <div ref={ref} className={cn('mx-auto max-w-2xl text-center', revealed && 'pp-animate-reveal')}>
          {f.sectionEyebrow && (
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">
              {f.sectionEyebrow}
            </p>
          )}
          <h2 className="mt-4 text-balance text-3xl font-semibold tracking-tight text-ink sm:text-5xl">
            {f.sectionTitle}
          </h2>
          {f.sectionSubtitle && (
            <p className="mt-4 text-base leading-relaxed text-ink-muted sm:text-lg">
              {f.sectionSubtitle}
            </p>
          )}
        </div>
        <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {f.items.map((item, i) => (
            <FeatureCard
              key={i}
              item={item}
              index={i}
              fallback={FALLBACK_ICONS[i % FALLBACK_ICONS.length]!}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  item,
  index,
  fallback,
}: {
  item: LandingFeature;
  index: number;
  fallback: IconComponent;
}): JSX.Element {
  const { ref, revealed } = useReveal<HTMLElement>();
  return (
    <article
      ref={ref}
      className={cn(
        'group relative overflow-hidden rounded-xl border border-rule bg-paper p-6 opacity-0 transition duration-300',
        'hover:-translate-y-1 hover:border-accent/50 hover:shadow-lg hover:shadow-accent/10',
        revealed && 'pp-animate-reveal',
      )}
      style={
        revealed
          ? { animationDelay: `${Math.min(index, 6) * 0.08}s`, opacity: 1 }
          : undefined
      }
    >
      {/* Subtle accent wash on hover */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-0 transition group-hover:opacity-100"
        style={{
          background:
            'radial-gradient(circle at top right, rgb(var(--color-accent) / 0.10), transparent 60%)',
        }}
      />
      <div className="relative">
        <div className="inline-flex size-10 items-center justify-center rounded-md bg-accent/10 text-accent ring-1 ring-accent/20 transition group-hover:bg-accent/15 group-hover:ring-accent/40">
          {IconFor(item.icon, fallback)}
        </div>
        <h3 className="mt-4 text-base font-semibold tracking-tight text-ink">{item.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-ink-muted">{item.description}</p>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// About — split layout with decorative animated panel
// ---------------------------------------------------------------------------

function AboutSection({ content }: { content: LandingContent }): JSX.Element {
  const a = content.about;
  const { ref, revealed } = useReveal<HTMLDivElement>();
  return (
    <section id="about" className="relative border-b border-rule/40 bg-paper-soft/30">
      <div
        ref={ref}
        className={cn(
          'mx-auto grid max-w-6xl gap-12 px-6 py-24 sm:py-32 lg:grid-cols-[1fr_1.4fr] lg:items-start',
          revealed && 'pp-animate-reveal',
        )}
      >
        <div className="lg:sticky lg:top-24">
          {a.eyebrow && (
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">
              {a.eyebrow}
            </p>
          )}
          <h2 className="mt-4 text-balance text-3xl font-semibold tracking-tight text-ink sm:text-5xl">
            {a.title}
          </h2>

          {/* Decorative animated logo block under the heading */}
          <div className="relative mt-8 hidden h-44 overflow-hidden rounded-xl border border-rule bg-paper-soft/60 lg:block">
            <div
              aria-hidden="true"
              className="pp-animate-pulse-glow absolute -right-12 -top-12 size-56 rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, rgb(var(--color-accent) / 0.35) 0%, transparent 65%)',
              }}
            />
            <div className="absolute inset-0 grid place-items-center">
              <img
                src="/peerless-logo.png"
                alt="Peerless"
                className="pp-animate-float h-12 w-auto select-none opacity-95 invert dark:invert-0 dark:drop-shadow-[0_4px_12px_rgba(0,0,0,0.4)]"
                draggable={false}
              />
            </div>
          </div>
        </div>
        <div className="space-y-5 text-base leading-relaxed text-ink-muted sm:text-lg">
          {a.body.split(/\n\n+/).map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

function ContactSection({ content }: { content: LandingContent }): JSX.Element {
  const c = content.contact;
  const { ref, revealed } = useReveal<HTMLDivElement>();
  return (
    <section id="contact" className="relative border-b border-rule/40">
      <div
        ref={ref}
        className={cn(
          'mx-auto grid max-w-6xl gap-10 px-6 py-24 sm:py-32 lg:grid-cols-[1fr_1.2fr]',
          revealed && 'pp-animate-reveal',
        )}
      >
        <div>
          {c.eyebrow && (
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-accent">
              {c.eyebrow}
            </p>
          )}
          <h2 className="mt-4 text-balance text-3xl font-semibold tracking-tight text-ink sm:text-5xl">
            {c.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-ink-muted sm:text-lg">{c.body}</p>

          {/* Auxiliary contact details — only the email defaults to non-null. */}
          <div className="mt-8 space-y-3">
            <ContactRow icon={<Mail className="size-4" />} label="Email">
              <a href={`mailto:${c.email}`} className="text-ink transition hover:text-accent">
                {c.email}
              </a>
            </ContactRow>
            {c.phone && (
              <ContactRow icon={<Phone className="size-4" />} label="Phone">
                <a href={`tel:${c.phone}`} className="text-ink transition hover:text-accent">
                  {c.phone}
                </a>
              </ContactRow>
            )}
            {c.address && (
              <ContactRow icon={<MapPin className="size-4" />} label="Address">
                <span className="text-ink">{c.address}</span>
              </ContactRow>
            )}
          </div>
        </div>

        <LeadForm />
      </div>
    </section>
  );
}

function ContactRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-rule bg-paper p-4 transition hover:border-accent/40">
      <div className="grid size-10 shrink-0 place-items-center rounded-md bg-accent/10 text-accent ring-1 ring-accent/20">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-muted">{label}</p>
        <p className="mt-0.5 text-sm">{children}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lead form — submits to POST /landing/leads (unauthenticated). On success we
// swap the form for a confirmation card so the visitor knows it went through.
// ---------------------------------------------------------------------------

interface LeadFormFields extends CreateLeadRequest {
  /** Honeypot field — bots fill it; humans never see it. */
  hp_company_size?: string;
}

function LeadForm(): JSX.Element {
  const [values, setValues] = useState<LeadFormFields>({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    hp_company_size: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof LeadFormFields, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const set = (k: keyof LeadFormFields) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      setValues((prev) => ({ ...prev, [k]: e.target.value }));
      if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
    };

  const validate = (): boolean => {
    const next: Partial<Record<keyof LeadFormFields, string>> = {};
    if (!values.name.trim()) next.name = 'Required';
    if (!values.email.trim()) next.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      next.email = 'Enter a valid email';
    if (!values.message.trim()) next.message = 'Required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    // Honeypot — silently drop bot submissions so they don't realise.
    if (values.hp_company_size && values.hp_company_size.length > 0) {
      setSubmitted(true);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body: CreateLeadRequest = {
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone?.trim() || undefined,
        company: values.company?.trim() || undefined,
        subject: values.subject?.trim() || undefined,
        message: values.message.trim(),
      };
      await apiRequest<{ ok: true; id: string }>('/landing/leads', {
        method: 'POST',
        body,
        unauthenticated: true,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again or email us directly.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-xl border border-rule bg-paper p-10 text-center">
        <div className="grid size-12 place-items-center rounded-full bg-positive/15 text-positive">
          <CheckCircle2 className="size-7" />
        </div>
        <h3 className="mt-4 text-xl font-semibold tracking-tight text-ink">
          Thanks — we got it.
        </h3>
        <p className="mt-2 max-w-sm text-sm text-ink-muted">
          We&apos;ll be in touch within one business day. In the meantime, your message
          is safely on its way to the team.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="rounded-xl border border-rule bg-paper p-6 shadow-lg shadow-accent/5 sm:p-8"
    >
      <h3 className="text-lg font-semibold tracking-tight text-ink">Send us a message</h3>
      <p className="mt-1 text-sm text-ink-muted">
        Tell us a little about what you&apos;re building — we&apos;ll get back to you.
      </p>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <LeadField label="Full name" required error={errors.name}>
          <input
            type="text"
            value={values.name}
            onChange={set('name')}
            maxLength={LEAD_LIMITS.nameMaxLen}
            autoComplete="name"
            className={inputClass(!!errors.name)}
          />
        </LeadField>
        <LeadField label="Work email" required error={errors.email}>
          <input
            type="email"
            value={values.email}
            onChange={set('email')}
            maxLength={LEAD_LIMITS.emailMaxLen}
            autoComplete="email"
            spellCheck={false}
            className={inputClass(!!errors.email)}
          />
        </LeadField>
        <LeadField label="Phone">
          <input
            type="tel"
            value={values.phone ?? ''}
            onChange={set('phone')}
            maxLength={LEAD_LIMITS.phoneMaxLen}
            autoComplete="tel"
            className={inputClass(false)}
          />
        </LeadField>
        <LeadField label="Company">
          <input
            type="text"
            value={values.company ?? ''}
            onChange={set('company')}
            maxLength={LEAD_LIMITS.companyMaxLen}
            autoComplete="organization"
            className={inputClass(false)}
          />
        </LeadField>
        <div className="sm:col-span-2">
          <LeadField label="Subject">
            <input
              type="text"
              value={values.subject ?? ''}
              onChange={set('subject')}
              maxLength={LEAD_LIMITS.subjectMaxLen}
              className={inputClass(false)}
            />
          </LeadField>
        </div>
        <div className="sm:col-span-2">
          <LeadField label="Message" required error={errors.message}>
            <textarea
              value={values.message}
              onChange={set('message')}
              rows={5}
              maxLength={LEAD_LIMITS.messageMaxLen}
              className={cn(inputClass(!!errors.message), 'resize-none')}
            />
          </LeadField>
        </div>
      </div>

      {/* Honeypot — hidden from users + ATs via inert + aria-hidden + position. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-[9999px] size-0 overflow-hidden opacity-0"
      >
        <label>
          Company size (leave blank)
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={values.hp_company_size ?? ''}
            onChange={set('hp_company_size')}
          />
        </label>
      </div>

      {submitError && (
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-danger">
          <AlertCircle className="size-3" />
          {submitError}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className={cn(
          'mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-accent px-5 py-2.5 text-sm font-medium text-accent-fg shadow-sm shadow-accent/30 transition',
          'hover:scale-[1.01] hover:shadow-md hover:shadow-accent/40',
          'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100',
        )}
      >
        {submitting ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Sending…
          </>
        ) : (
          <>
            <Send className="size-4" />
            Send message
          </>
        )}
      </button>
    </form>
  );
}

function LeadField({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink">
        {label}
        {required && <span className="ml-0.5 text-danger">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      {error && (
        <p className="mt-1 flex items-center gap-1 text-[11px] text-danger">
          <AlertCircle className="size-3" />
          {error}
        </p>
      )}
    </label>
  );
}

function inputClass(hasError: boolean): string {
  return cn(
    'block w-full rounded-tight border bg-paper-soft px-3 py-2 text-sm text-ink outline-none transition',
    'focus:border-accent',
    hasError ? 'border-danger' : 'border-rule',
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function Footer({ content }: { content: LandingContent }): JSX.Element {
  return (
    <footer className="border-t border-rule/60 bg-paper">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-10 text-xs text-ink-muted">
        <nav
          aria-label="Footer"
          className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 sm:justify-start"
        >
          <Link to="/about" className="transition hover:text-ink">
            About us
          </Link>
          <Link to="/contact" className="transition hover:text-ink">
            Contact us
          </Link>
          <a
            href="/wiki.html"
            target="_blank"
            rel="noopener noreferrer"
            className="transition hover:text-ink"
          >
            Wiki
          </a>
          <a
            href="/user-guide-wiki.html"
            target="_blank"
            rel="noopener noreferrer"
            className="transition hover:text-ink"
          >
            User Guide
          </a>
          <Link to="/login" className="transition hover:text-ink">
            Sign in
          </Link>
        </nav>
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex items-center gap-2.5">
            <img
              src="/peerless-logo.png"
              alt="Peerless"
              className="h-5 w-auto opacity-80 select-none invert dark:invert-0 dark:drop-shadow-[0_1px_1px_rgba(0,0,0,0.4)]"
              draggable={false}
            />
            <span className="text-ink/80">AI Portal</span>
          </div>
          <p>{content.footer.tagline}</p>
        </div>
      </div>
    </footer>
  );
}
